import { AccessTokenClaims, UserInfoResponse, UserScope } from '@logto/react';
import { Organisation, UserCustomData } from '../types/user';
import { FAQ, FeatureFlag, SystemRoles } from '../types/generic';
import { isLegacyOp } from './operations';
import { initialSystemState, SystemState } from '../store/system/systemSlice';
import { getCustomData, getFAQs, getForceDetails, getUpdateNotice, getUserOrgCustomData } from './api';
import { sanitiseString } from './string';
import { ForceDetails } from '../types/upload';

export const requiredLogToResources = ['urn:logto:resource:organizations', import.meta.env.VITE_LOGTO_IDENTIFIER];

export const requiredLogToScopes = [
	UserScope.Email,
	UserScope.Roles,
	'urn:logto:scope:organizations',
	UserScope.OrganizationRoles,
	'assign:operation',
	'create:operation',
	'delete:operation',
	'rename:operation',
	'view_all:operation',
	'assign:role',
	'upload:axiom',
	'upload:stellar',
	'enrich:language',
];

export const getUserOrgs = async (
	getOrganizationTokenClaims: (organizationId: string) => Promise<AccessTokenClaims | undefined>,
	info?: UserInfoResponse | null
): Promise<Organisation[]> => {
	const orgs: Organisation[] = [];

	if (info?.organization_roles && info?.organization_data) {
		await Promise.all(
			info.organization_data.map(async (org_data) => {
				if (info?.organization_roles) {
					if (info.organization_roles.some((role) => role.startsWith(org_data.id))) {
						const orgPermissions = await getOrganizationTokenClaims(org_data.id);

						const orgRoles: string[] = [];
						// role names are formatted as `id:name`
						info.organization_roles.forEach((role) => {
							if (role.startsWith(org_data.id)) {
								orgRoles.push(role.substring(`${org_data.id}:`.length));
							}
						});

						orgs.push({
							name: org_data.name,
							id: org_data.id,
							roles: orgRoles,
							permissions: orgPermissions?.scope ? orgPermissions.scope.split(' ') : [],
						});
					} else {
						orgs.push({
							name: org_data.name,
							id: org_data.id,
							roles: [],
							permissions: [],
						});
					}
				}
			})
		);
	}

	return [...orgs].sort((a, b) => a.name.localeCompare(b.name));
};

export const getUserSystemState = async (accessToken: string): Promise<SystemState> => {
	const [customDataResponse, updateNoticeResponse, faqResponse, forceResponse] = await Promise.all([
		getCustomData(accessToken),
		getUpdateNotice(accessToken),
		getFAQs(accessToken),
		getForceDetails(accessToken),
	]);

	let setupData: SystemState = initialSystemState;

	if (customDataResponse.status === 200) {
		const customData = (await customDataResponse.json()) as UserCustomData;
		setupData = {
			...setupData,
			theme: customData.theme || setupData.theme,
			touAgreed: customData.touAgreed || setupData.touAgreed,
			keyphrasesHelpViewed: customData.keyphrasesHelpViewed,
		};
	}
	if (updateNoticeResponse.status === 200) {
		const updateNotice = (await updateNoticeResponse.json()) as { title?: string; desc?: string };
		setupData = {
			...setupData,
			updateNoticeTitle: updateNotice.title,
			updateNoticeDesc: updateNotice.desc,
		};
	}
	if (faqResponse.status === 200) {
		const rawFAQs = (await faqResponse.json()) as { question: string; answer: string }[];

		const faqs: FAQ[] = rawFAQs.map((faq) => {
			return {
				question: faq.question,
				answer: faq.answer,
				link: encodeURI(sanitiseString(faq.question)),
			};
		});

		setupData = {
			...setupData,
			faqs,
		};
	}
	if (forceResponse.status === 200) {
		// when modifying this, remember that anything here can be SEEN and MANIPULATED by the user
		// ie, EDH Reference is left out deliberately to prevent impersonation
		const rawDetails = (await forceResponse.json()) as {
			force_name: string;
			force_aliases: string[];
			use_cases: Record<string, { rrd_days: number }>;
			rrd_days?: number;
		}[];

		const forceDetails: ForceDetails = Object.fromEntries(
			rawDetails.map((details) => {
				// TODO: 13/04 fallback for legacy force_data entries- remove by 01/06/2026
				if (details.use_cases) {
					return [
						details.force_name,
						{
							forceAliases: [details.force_name, ...details.force_aliases],
							useCases: Object.fromEntries(Object.entries(details.use_cases).map(([key, value]) => [key, { rrdDays: value.rrd_days }])),
						},
					];
				} else if (details.rrd_days) {
					return [
						details.force_name,
						{
							forceAliases: [details.force_name, ...details.force_aliases],
							useCases: { CSAE: { rrd_days: details.rrd_days } },
						},
					];
				} else {
					return [];
				}
			})
		);

		setupData = {
			...setupData,
			forceDetails,
		};
	}

	return setupData;
};

export const getUserFeatureFlags = async (accessToken: string): Promise<FeatureFlag[]> => {
	const featureFlags: FeatureFlag[] = [];
	const res = await getUserOrgCustomData(accessToken);
	if (res.ok) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const response = (await res.json()) as any[];
		const flagsResponse = response.flatMap((customData) => customData['featureFlags'] && customData['featureFlags']);

		if (flagsResponse.includes('newUploadForm')) {
			featureFlags.push(FeatureFlag.NEW_UPLOAD_FORM);
		}
	}

	return featureFlags;
};

export const getUserSystemRoles = (organisations: Organisation[], logtoRoles?: string[]): SystemRoles[] => {
	const userRoles: SystemRoles[] = [];

	const hasUploadPermission = logtoRoles?.some((option) => option.endsWith('-upload')) || false;
	const hasLegacyPermission = logtoRoles?.some((option) => isLegacyOp(option)) || false;
	const hasViewPermission = logtoRoles?.some((option) => option.endsWith('-view')) || false;
	const hasAdminPermission = logtoRoles?.some((option) => option === 'Prometheus Administrators') || false;

	const hasCreatePermission = organisations.some((org) =>
		org.permissions.some((perm) => {
			return perm.includes('create:operation');
		})
	);
	const hasManagePermission = organisations.some((org) =>
		org.permissions.some((perm) => {
			return perm.includes(':operation') && perm !== 'create:operation';
		})
	);
	const hasManageRolePermission = organisations.some((org) =>
		org.permissions.some((perm) => {
			return perm === 'assign:role';
		})
	);
	const hasDeveloperPermission = organisations.some((org) => org.name === 'Prometheus Development Team');
	const hasMSHTPermission = organisations.some((org) => org.name === 'Northumbria Police');
	const hasAxiomUploadPermission = organisations.some((org) =>
		org.permissions.some((perm) => {
			return perm === 'upload:axiom';
		})
	);

	const hasStellarPermission = organisations.some((org) =>
		org.permissions.some((perm) => {
			return perm === 'upload:stellar';
		})
	);

	const hasLanguagePermission = organisations.some((org) =>
		org.permissions.some((perm) => {
			return perm === 'enrich:language';
		})
	);

	if (hasDeveloperPermission) {
		userRoles.push('DEVELOPER');
	}
	if (hasUploadPermission || hasLegacyPermission) {
		userRoles.push('UPLOAD');
	}
	if (hasViewPermission || hasLegacyPermission) {
		userRoles.push('VIEW');
	}
	if (hasCreatePermission) {
		userRoles.push('CREATE_OP');
	}
	if (hasManagePermission) {
		userRoles.push('MANAGE_OP');
	}
	if (hasManageRolePermission) {
		userRoles.push('MANAGE_ORG');
	}
	if (hasAdminPermission) {
		userRoles.push('ADMINISTRATOR');
	}
	if (hasMSHTPermission || hasDeveloperPermission) {
		userRoles.push('MSHT');
	}
	if (hasAxiomUploadPermission) {
		userRoles.push('UPLOAD_AXIOM');
	}
	if (hasStellarPermission) {
		userRoles.push('UPLOAD_STELLAR');
	}
	if (hasLanguagePermission) {
		userRoles.push('ENRICH_LANGUAGE');
	}

	return userRoles;
};
